/**
 * useBatchSelection Hook
 *
 * Encapsulates the logic for:
 * - Managing selected submission IDs
 * - Handling select all / deselect all on current page
 * - Checking selection state for UI rendering
 *
 * Follows Single Responsibility Principle - only handles selection logic
 */

import { useMemo, useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  selectedIdsAtom,
  toggleSelectionAtom,
  selectAllOnPageAtom,
  clearSelectionAtom,
} from "../store";
import type { SubmissionItem } from "../store/types";

interface UseBatchSelectionResult {
  /** Set of currently selected submission IDs */
  selectedIds: Set<number>;
  /** Number of selected items */
  selectedCount: number;
  /** Whether any items are selected */
  hasSelection: boolean;
  /** Toggle selection for a single submission */
  toggleSelection: (id: number) => void;
  /** Toggle select all on current page */
  toggleSelectAll: (pageSubmissions: SubmissionItem[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Check if a specific submission is selected */
  isSelected: (id: number) => boolean;
  /** Check if all items on current page are selected */
  isAllSelectedOnPage: (pageSubmissions: SubmissionItem[]) => boolean;
  /** Check if some items on current page are selected */
  isSomeSelectedOnPage: (pageSubmissions: SubmissionItem[]) => boolean;
  /** Get checkbox state for select all ("checked" | "indeterminate" | false) */
  getSelectAllState: (
    pageSubmissions: SubmissionItem[]
  ) => boolean | "indeterminate";
}

/**
 * Hook for managing batch selection of submissions
 *
 * @example
 * ```tsx
 * const {
 *   selectedIds,
 *   toggleSelection,
 *   toggleSelectAll,
 *   getSelectAllState
 * } = useBatchSelection();
 *
 * // In table header
 * <Checkbox
 *   checked={getSelectAllState(paginatedSubmissions)}
 *   onCheckedChange={() => toggleSelectAll(paginatedSubmissions)}
 * />
 *
 * // In table row
 * <Checkbox
 *   checked={isSelected(submission.db_id)}
 *   onCheckedChange={() => toggleSelection(submission.db_id)}
 * />
 * ```
 */
export function useBatchSelection(): UseBatchSelectionResult {
  const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);
  const toggleSelectionAction = useSetAtom(toggleSelectionAtom);
  const selectAllOnPageAction = useSetAtom(selectAllOnPageAtom);
  const clearSelectionAction = useSetAtom(clearSelectionAtom);

  const selectedCount = selectedIds.size;
  const hasSelection = selectedCount > 0;

  // Toggle single selection
  const toggleSelection = useCallback(
    (id: number) => {
      toggleSelectionAction(id);
    },
    [toggleSelectionAction]
  );

  // Toggle select all on page
  const toggleSelectAll = useCallback(
    (pageSubmissions: SubmissionItem[]) => {
      const pageIds = pageSubmissions
        .map((s) => s.db_id)
        .filter((id): id is number => id !== undefined);
      selectAllOnPageAction(pageIds);
    },
    [selectAllOnPageAction]
  );

  // Clear all selections
  const clearSelection = useCallback(() => {
    clearSelectionAction();
  }, [clearSelectionAction]);

  // Check if specific submission is selected
  const isSelected = useCallback(
    (id: number) => selectedIds.has(id),
    [selectedIds]
  );

  // Check if all items on page are selected
  const isAllSelectedOnPage = useCallback(
    (pageSubmissions: SubmissionItem[]) => {
      const pageIds = pageSubmissions
        .map((s) => s.db_id)
        .filter((id): id is number => id !== undefined);
      if (pageIds.length === 0) return false;
      return pageIds.every((id) => selectedIds.has(id));
    },
    [selectedIds]
  );

  // Check if some items on page are selected
  const isSomeSelectedOnPage = useCallback(
    (pageSubmissions: SubmissionItem[]) => {
      const pageIds = pageSubmissions
        .map((s) => s.db_id)
        .filter((id): id is number => id !== undefined);
      return pageIds.some((id) => selectedIds.has(id));
    },
    [selectedIds]
  );

  // Get checkbox state for select all
  const getSelectAllState = useCallback(
    (pageSubmissions: SubmissionItem[]): boolean | "indeterminate" => {
      const allSelected = isAllSelectedOnPage(pageSubmissions);
      const someSelected = isSomeSelectedOnPage(pageSubmissions);

      if (allSelected) return true;
      if (someSelected) return "indeterminate";
      return false;
    },
    [isAllSelectedOnPage, isSomeSelectedOnPage]
  );

  return {
    selectedIds,
    selectedCount,
    hasSelection,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    isSelected,
    isAllSelectedOnPage,
    isSomeSelectedOnPage,
    getSelectAllState,
  };
}
